import type { AddressStepValues } from "@/lib/onboarding/onboarding-schemas";
import type { CustomerRegisterAddressInput } from "@/lib/auth/backend-auth-client";

/**
 * ISO 3166-1 alpha-2 country code. Hardcoded for MVP (Brazil-only product).
 * When multi-country support is added, this should be derived from a lookup
 * (e.g. country selector → ISO code) instead of the display label.
 */
const COUNTRY_CODE = "BR";

/**
 * Maps frontend address form values to the backend's address DTO, always as the primary address.
 *
 * Field remap:
 *   state    → province
 *   zipCode  → postalCode
 *   street   → address
 *   country  → "BR" (ISO code, not the display label "Brasil")
 *
 * Always sets `isPrimary: true` (MVP: one address per customer).
 *
 * `province` is upper-cased so the persisted UF matches the one `/checkout`
 * transacts (`toCheckoutAddress` also upper-cases) — the form's `state` field
 * is free-text, so without this a buyer typing `sp` would persist `sp` yet pay
 * with `SP`.
 *
 * **`type`, `useForBilling` and `useForDelivery` travel (2026-10-14).** They used
 * to be dropped here on purpose, because `CustomerAddressDto` had no such fields
 * and the backend's whitelist refuses what it does not declare. PR #64 added all
 * three as **required** (`@IsEnum`/`@IsBoolean`, no `@IsOptional`), plus the
 * constraint that an address must serve billing and/or delivery. From the moment
 * that reached the API, dropping them turned every registration into a 400:
 *
 *   addresses.0.type must be one of the following values: RESIDENTIAL, COMMERCIAL
 *   addresses.0.Endereço deve servir para cobrança e/ou entrega (ao menos um)
 *   addresses.0.useForBilling must be a boolean value
 *   addresses.0.useForDelivery must be a boolean value
 *
 * The form already collected the three (`addressStepSchema`), including the same
 * "at least one use" refinement the backend now enforces, so this is a wiring fix
 * and not a new question to the person filling the form.
 */
export function toBackendAddress(values: AddressStepValues): CustomerRegisterAddressInput {
  return {
    country: COUNTRY_CODE,
    province: values.state.toUpperCase(),
    city: values.city,
    neighborhood: values.neighborhood,
    postalCode: values.zipCode,
    address: values.street,
    number: values.number,
    complement: values.complement || undefined,
    type: values.type,
    useForBilling: values.useForBilling,
    useForDelivery: values.useForDelivery,
    isPrimary: true,
  };
}
